/**
 * /admin/codes — invite/prepaid code management (OCP-1).
 *
 * Same filter-sidebar + table layout as the other admin pages.
 * Operator can:
 *  - Filter by kind / tier / category / paid / unused-only
 *  - Click "Export CSV" → triggers a download of the filtered set
 *    (uses the same querystring as the list)
 *  - Click "Generate codes" → opens a modal with the template form
 *    (count + kind/tier/category/duration/seats/maxUses/paid/note)
 *    → POSTs to /v1/admin/codes/bulk → toast "N generated" → table
 *    refetches.
 *
 * Code-template-storage (saved presets like "Beta-Tester 6mo") is
 * out of scope on the backend (no `code_templates` table). The
 * modal collects the template fields each time. If usage proves
 * presets-worth-a-table, we add a backend later without changing
 * the modal's form shape.
 */
import { Link } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import { api, ApiError } from '../lib/api';
import { sensitiveReadUrl } from '../lib/sensitive-read';
import {
  DEFAULT_LICENSE_DURATION,
  LICENSE_DURATION_OPTIONS,
  type LicenseDurationValue,
} from '../lib/license-duration';
import { formatRelative } from './HomePage';
import { useTransientState } from '../lib/useTransientState';

interface CodeItem {
  id: string;
  code: string;
  /** OCP-510 - ready-to-share redeem link, same as the invite email's CTA. */
  redeem_url: string;
  kind: string;
  tier: string;
  category: string;
  duration: string | null;
  duration_months: number | null;
  seats: number | null;
  paid: boolean;
  paid_amount_eur: number | null;
  storage_quota_gb_override: number | null;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  created_at: string;
  created_by: string;
  note: string | null;
  waitlist_entry_id: string | null;
  region: string | null;
  recommended_team_size: string | null;
}

const KIND_OPTIONS = ['invite', 'prepaid'] as const;
type KindOption = (typeof KIND_OPTIONS)[number];

const TIER_OPTIONS = ['team', 'business'] as const;
type TierOption = (typeof TIER_OPTIONS)[number];

const CATEGORY_OPTIONS = [
  'early_adopter',
  'beta_tester',
  'friend_family',
  'goodwill_refund',
  'conference_giveaway',
  'partner',
  'direct_sale',
] as const;
type CategoryOption = (typeof CATEGORY_OPTIONS)[number];

interface Filters {
  kind: KindOption | '';
  tier: TierOption | '';
  category: CategoryOption | '';
  paid: '' | 'true' | 'false';
  unused_only: boolean;
}

const EMPTY_FILTERS: Filters = {
  kind: '',
  tier: '',
  category: '',
  paid: '',
  unused_only: false,
};

export function AdminCodesPage() {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<CodeItem[] | null>(null);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useTransientState<string | null>(null, 4000);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [editing, setEditing] = useState<CodeItem | null>(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.kind) params.set('kind', filters.kind);
    if (filters.tier) params.set('tier', filters.tier);
    if (filters.category) params.set('category', filters.category);
    if (filters.paid) params.set('paid', filters.paid);
    if (filters.unused_only) params.set('unused_only', 'true');
    return params.toString() ? `?${params.toString()}&limit=500` : '?limit=500';
  }, [filters]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const r = await api<{ total: number; items: CodeItem[] }>(`/v1/admin/codes${queryString}`);
      setItems(r.items);
      setTotal(r.total);
    } catch (err) {
      if (err instanceof ApiError) setError(`Could not load codes (HTTP ${err.status}).`);
      else setError('Could not load codes.');
    }
  }, [queryString]);

  useEffect(() => {
    void load();
  }, [load]);

  function downloadCsv() {
    // CSV download is a plain GET — open in a new tab so the browser
    // handles the Content-Disposition: attachment header.
    // OCP-930 - every matching redeemable code in one file: a sensitive read with a reason.
    const url = sensitiveReadUrl(`/v1/admin/codes/export.csv${queryString.replace(/limit=500&?/, '').replace(/[?&]$/, '')}`, 'The code export');
    if (url) window.open(url, '_blank');
  }

  async function onGenerate(template: BulkGenerateTemplate, count: number) {
    setError(null);
    try {
      const r = await api<{ generated: number }>('/v1/admin/codes/bulk', {
        method: 'POST',
        body: { count, template },
      });
      setActionMessage(`Generated ${r.generated} code${r.generated === 1 ? '' : 's'}.`);
      setGenerateOpen(false);
      await load();
      // Clear the toast after 4 seconds.
    } catch (err) {
      if (err instanceof ApiError) setError(`Generation failed (HTTP ${err.status}).`);
      else setError('Generation failed.');
    }
  }

  async function onEdit(id: string, patch: Record<string, unknown>) {
    setError(null);
    try {
      await api(`/v1/admin/codes/${id}`, { method: 'PATCH', body: patch });
      setActionMessage('Code updated.');
      setEditing(null);
      await load();
    } catch (err) {
      if (err instanceof ApiError) {
        // Surface the two guarded 4xx cases with a helpful message.
        const hint =
          err.status === 409
            ? 'Grant fields (tier, duration, seats, storage, paid) are locked once the code has been redeemed - tick "Override the grant lock" and give a reason to change them.'
            : err.status === 400
              ? 'Invalid change (e.g. max uses below the number already used, or the override reason is missing / shorter than 10 characters).'
              : `HTTP ${err.status}`;
        setError(`Update failed: ${hint}`);
      } else setError('Update failed.');
    }
  }

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <aside className="w-full shrink-0 lg:w-56">
        <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 shadow-sm">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Filters
            </h2>
            {hasFilters(filters) && (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_FILTERS)}
                className="text-xs text-brand-500 hover:text-brand-600"
              >
                Reset
              </button>
            )}
          </div>
          <FilterSelect
            label="Kind"
            value={filters.kind}
            options={KIND_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, kind: v }))}
          />
          <FilterSelect
            label="Tier"
            value={filters.tier}
            options={TIER_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, tier: v }))}
          />
          <FilterSelect
            label="Category"
            value={filters.category}
            options={CATEGORY_OPTIONS}
            onChange={(v) => setFilters((f) => ({ ...f, category: v }))}
          />
          <label className="mb-3 flex flex-col gap-1 text-xs">
            <span className="text-gray-600 dark:text-gray-400">Paid</span>
            <select
              value={filters.paid}
              onChange={(e) =>
                setFilters((f) => ({ ...f, paid: e.target.value as Filters['paid'] }))
              }
              className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              <option value="">All</option>
              <option value="true">Paid</option>
              <option value="false">Free</option>
            </select>
          </label>
          <label className="mt-2 flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={filters.unused_only}
              onChange={(e) => setFilters((f) => ({ ...f, unused_only: e.target.checked }))}
              className="h-4 w-4 accent-brand-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Unused only</span>
          </label>
        </div>
      </aside>

      <main className="min-w-0 flex-1">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-baseline sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Codes</h1>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              {items === null
                ? 'Loading…'
                : `${total} ${total === 1 ? 'code' : 'codes'} matching the current filters`}
            </p>
            {/* OCP-911 - what happens to the workspaces these codes granted once the months run out. */}
            <p className="mt-1 text-sm">
              <Link to="/code-grants" className="text-brand-500 hover:underline">
                Workspaces from codes: grant ends, grace, reminders sent
              </Link>
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={downloadCsv}
              disabled={items === null || items.length === 0}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:cursor-not-allowed disabled:text-gray-400"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={() => setGenerateOpen(true)}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 active:bg-brand-700"
            >
              Generate codes
            </button>
          </div>
        </div>

        {actionMessage && (
          <div
            role="status"
            className="mb-4 rounded border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/30 px-3 py-2 text-sm text-emerald-800 dark:text-emerald-300"
          >
            {actionMessage}
          </div>
        )}
        {error && (
          <div
            role="alert"
            className="mb-4 flex items-center justify-between rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300"
          >
            <span>{error}</span>
            <button
              type="button"
              onClick={() => void load()}
              className="rounded border border-red-300 dark:border-red-700 bg-white dark:bg-gray-900 px-2 py-0.5 text-xs text-red-700 dark:text-red-300 hover:bg-red-100"
            >
              Retry
            </button>
          </div>
        )}

        <CodesTable items={items} onEdit={setEditing} />
      </main>

      {generateOpen && (
        <GenerateModal onCancel={() => setGenerateOpen(false)} onConfirm={onGenerate} />
      )}
      {editing && (
        <EditCodeModal code={editing} onCancel={() => setEditing(null)} onSave={onEdit} />
      )}
    </div>
  );
}

function hasFilters(f: Filters): boolean {
  return Boolean(f.kind || f.tier || f.category || f.paid || f.unused_only);
}

function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | '';
  options: readonly T[];
  onChange: (v: T | '') => void;
}) {
  return (
    <label className="mb-3 flex flex-col gap-1 text-xs">
      <span className="text-gray-600 dark:text-gray-400">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value as T | '')}
        className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}

function CodesTable({
  items,
  onEdit,
}: {
  items: CodeItem[] | null;
  onEdit: (code: CodeItem) => void;
}) {
  if (items === null) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        Loading…
      </div>
    );
  }
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-sm text-gray-500">
        No codes match the current filters. Use the Generate-codes button to create some.
      </div>
    );
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs font-medium uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Code</th>
            <th className="px-3 py-2">Kind</th>
            <th className="px-3 py-2">Tier</th>
            <th className="px-3 py-2">Category</th>
            <th className="px-3 py-2">Region</th>
            <th className="px-3 py-2">Team</th>
            <th className="px-3 py-2">Duration</th>
            <th className="px-3 py-2 text-right">Paid €</th>
            <th className="px-3 py-2 text-right">Used</th>
            <th className="px-3 py-2 text-right">Created</th>
            <th className="px-3 py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {items.map((it) => (
            <tr key={it.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/60">
              <td className="px-3 py-2 font-mono text-xs">
                <div className="flex flex-col items-start gap-1">
                  <CopyCode code={it.code} />
                  <CopyLink url={it.redeem_url} />
                </div>
              </td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.kind}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.tier}</td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.category.replace(/_/g, ' ')}</td>
              <td className="px-3 py-2 text-xs">
                {it.region ? (
                  <span className="rounded bg-blue-100 px-1.5 py-0.5 font-mono text-blue-800 dark:text-blue-300">
                    {it.region}
                  </span>
                ) : (
                  <span className="text-gray-400">auto</span>
                )}
              </td>
              <td className="px-3 py-2 text-xs">
                {it.recommended_team_size ? (
                  <span className="rounded bg-purple-100 px-1.5 py-0.5 font-mono text-purple-800">
                    {it.recommended_team_size}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
              <td className="px-3 py-2 text-gray-600 dark:text-gray-400">{it.duration ?? 'lifetime'}</td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                {it.paid ? (it.paid_amount_eur ?? 0).toFixed(2) : '—'}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                {it.used_count}/{it.max_uses}
              </td>
              <td className="px-3 py-2 text-right tabular-nums text-gray-600 dark:text-gray-400">
                {formatRelative(it.created_at)}
              </td>
              <td className="px-3 py-2 text-right">
                <button
                  type="button"
                  onClick={() => onEdit(it)}
                  className="rounded border border-gray-300 dark:border-gray-700 px-2 py-0.5 text-xs text-gray-700 dark:text-gray-200 hover:border-brand-500 hover:text-brand-600"
                >
                  Edit
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CopyCode({ code }: { code: string }) {
  const [copied, setCopied] = useTransientState(false, 1200);
  function onCopy() {
    void navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
    });
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      title="Click to copy"
      className="text-gray-900 dark:text-gray-100 hover:text-brand-600"
    >
      {code}
      {copied && <span className="ml-2 text-[10px] text-emerald-600">copied</span>}
    </button>
  );
}

/**
 * OCP-510 - copy the full shareable redeem link (`<account>/redeem?code=<CODE>`,
 * identical to the invite email's CTA) so the operator can paste a one-click
 * invite to someone instead of the bare code.
 */
function CopyLink({ url }: { url: string }) {
  const [copied, setCopied] = useTransientState(false, 1200);
  function onCopy() {
    void navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
    });
  }
  return (
    <button
      type="button"
      onClick={onCopy}
      title={url}
      className="font-sans text-[10px] text-brand-600 hover:text-brand-700 dark:text-brand-400"
    >
      {copied ? 'link copied' : 'Copy invite link'}
    </button>
  );
}

// ---- Generate modal --------------------------------------------------

interface BulkGenerateTemplate {
  kind: KindOption;
  tier: TierOption;
  category: CategoryOption;
  duration: LicenseDurationValue;
  seats?: number;
  maxUses?: number;
  paid?: boolean;
  paidAmountEur?: number;
  note?: string;
  region?: string;
  recommendedTeamSize?: string;
}

const TEAM_SIZE_OPTIONS = ['', '1-9', '10-49', '50-249', '250-999', '1000+'] as const;

interface RegionAvailability {
  region: string;
  usable: boolean;
  /** OCP-552: the two auto-scale reasons retired with the auto-scaler. */
  reason:
    | 'capacity-available'
    | 'no-workers'
    | 'all-full';
  capacity_summary: {
    active_servers: number;
    accepts_new_servers: number;
    tenant_count_sum: number;
    /**
     * OCP-551 class-sweep: despite the name, the API already sums the
     * per-server EFFECTIVE cap here (region-availability.ts uses the same
     * `effectiveCapSql` the OCP-436 scheduler gates on), not the static
     * `max_tenants` column - so a fleet-wide sum does not overstate
     * capacity. Not currently rendered in this UI; keep this comment if
     * that changes so the field isn't mistaken for a static sum.
     */
    max_tenants_sum: number;
  };
}

const REASON_LABELS: Record<RegionAvailability['reason'], string> = {
  'capacity-available': 'capacity available',
  'no-workers': 'no workers',
  'all-full': 'all workers full',
};

function GenerateModal({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: (template: BulkGenerateTemplate, count: number) => Promise<void>;
}) {
  const [count, setCount] = useState<number>(10);
  const [kind, setKind] = useState<KindOption>('invite');
  const [tier, setTier] = useState<TierOption>('team');
  const [category, setCategory] = useState<CategoryOption>('beta_tester');
  const [duration, setDuration] = useState<LicenseDurationValue>(DEFAULT_LICENSE_DURATION);
  const [paid, setPaid] = useState<boolean>(false);
  const [paidAmountEur, setPaidAmountEur] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [region, setRegion] = useState<string>('');
  const [recommendedTeamSize, setRecommendedTeamSize] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  // OCP-220: region-availability dropdown for the region-override
  // field. Lists every region known to the system; unusable ones
  // (no workers / all full) render disabled
  // with the reason in the label so the operator doesn't accidentally
  // pin a code to a region that can't accept tenants right now.
  const [regions, setRegions] = useState<RegionAvailability[] | null>(null);
  useEffect(() => {
    void api<{ items: RegionAvailability[] }>('/v1/admin/regions/availability')
      .then((r) => setRegions(r.items))
      .catch(() => setRegions([])); // fall back silently — input becomes a plain text fallback
  }, []);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const template: BulkGenerateTemplate = {
        kind,
        tier,
        category,
        duration,
        paid,
        paidAmountEur:
          paid && paidAmountEur.trim() ? Number(paidAmountEur) : undefined,
        note: note.trim() || undefined,
        region: region.trim() || undefined,
        recommendedTeamSize: recommendedTeamSize.trim() || undefined,
      };
      await onConfirm(template, count);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Generate codes</h2>
          <p className="mt-0.5 text-xs text-gray-500">
            Codes are issued immediately and available in the table after refresh.
          </p>
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
          <ModalRow label="How many">
            <input
              type="number"
              min={1}
              max={500}
              value={count}
              onChange={(e) => setCount(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </ModalRow>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ModalRow label="Kind">
              <SelectLike value={kind} options={KIND_OPTIONS} onChange={setKind} />
            </ModalRow>
            <ModalRow label="Tier">
              <SelectLike value={tier} options={TIER_OPTIONS} onChange={setTier} />
            </ModalRow>
          </div>
          <ModalRow label="Category">
            <SelectLike value={category} options={CATEGORY_OPTIONS} onChange={setCategory} />
          </ModalRow>
          <ModalRow label="Duration">
            <select
              data-testid="duration-select"
              value={duration}
              onChange={(e) => setDuration(e.target.value as LicenseDurationValue)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              {LICENSE_DURATION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </ModalRow>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={paid}
              onChange={(e) => setPaid(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Paid code</span>
          </label>
          {paid && (
            <ModalRow label="Amount EUR">
              <input
                type="number"
                step="0.01"
                min={0}
                value={paidAmountEur}
                onChange={(e) => setPaidAmountEur(e.target.value)}
                placeholder="e.g. 199.00"
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            </ModalRow>
          )}
          <ModalRow label="Recommended team-size (optional)">
            <select
              value={recommendedTeamSize}
              onChange={(e) => setRecommendedTeamSize(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              {TEAM_SIZE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o || '(no preselection — customer picks)'}
                </option>
              ))}
            </select>
            <span className="mt-0.5 text-[10px] text-gray-500">
              Pre-selects the team-size bucket in the redeem flow. The
              customer can override; mismatches are logged. Drives both
              the tier-recommendation surfaced in the response AND the
              capacity-weight against `vps_servers.max_tenants`.
            </span>
          </ModalRow>
          <ModalRow label="Region override (optional)">
            {regions === null ? (
              <select
                disabled
                className="w-full rounded border border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2 py-1.5 text-sm"
              >
                <option>Loading regions…</option>
              </select>
            ) : regions.length === 0 ? (
              // Endpoint failed or no regions known → fall back to free
              // text input so the form stays usable in a degraded state.
              <input
                type="text"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. us-east-1"
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            ) : (
              <select
                data-testid="region-override-select"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              >
                <option value="">— auto-detect via Geo-IP / default —</option>
                {regions.map((r) => (
                  <option
                    key={r.region}
                    value={r.region}
                    disabled={!r.usable}
                  >
                    {r.region}
                    {r.usable ? '' : ` (${REASON_LABELS[r.reason]})`}
                  </option>
                ))}
              </select>
            )}
            <span className="mt-0.5 text-[10px] text-gray-500">
              Pins every tenant redeemed from these codes to the named region.
              Regions with no free slot are disabled - flipping a server's
              accepts_new or registering a new worker re-enables them.
            </span>
          </ModalRow>
          <ModalRow label="Note (internal)">
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="e.g. first beta wave"
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </ModalRow>
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {submitting ? 'Generating…' : `Generate ${count}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Map a stored duration_months back to the select's enum value (null = lifetime). */
const MONTHS_TO_DURATION: Record<number, LicenseDurationValue> = {
  1: '1_month',
  3: '3_months',
  6: '6_months',
  12: '1_year',
  24: '2_years',
};
function durationValueFromMonths(months: number | null): LicenseDurationValue {
  return months == null ? 'lifetime' : (MONTHS_TO_DURATION[months] ?? 'lifetime');
}

/**
 * OCP-511 - edit an existing code's attributes. Grant fields (tier, duration,
 * seats, storage, paid) are DISABLED once the code has been redeemed
 * (used_count > 0) - editing them would not change the already-provisioned
 * tenant - and are then omitted from the PATCH so the server never 409s.
 * Validity (max uses, expiry) + assignment (region, category, team size, note)
 * stay editable. The code string itself is not editable.
 */
function EditCodeModal({
  code,
  onCancel,
  onSave,
}: {
  code: CodeItem;
  onCancel: () => void;
  onSave: (id: string, patch: Record<string, unknown>) => Promise<void> | void;
}) {
  const used = code.used_count > 0;
  const [tier, setTier] = useState<TierOption>(code.tier as TierOption);
  const [category, setCategory] = useState<CategoryOption>(code.category as CategoryOption);
  const [duration, setDuration] = useState<LicenseDurationValue>(
    durationValueFromMonths(code.duration_months),
  );
  const [paid, setPaid] = useState<boolean>(code.paid);
  const [paidAmountEur, setPaidAmountEur] = useState<string>(
    code.paid_amount_eur != null ? String(code.paid_amount_eur) : '',
  );
  const [seats, setSeats] = useState<string>(code.seats != null ? String(code.seats) : '');
  const [storage, setStorage] = useState<string>(
    code.storage_quota_gb_override != null ? String(code.storage_quota_gb_override) : '',
  );
  const [maxUses, setMaxUses] = useState<number>(code.max_uses);
  const [expiresAt, setExpiresAt] = useState<string>(
    code.expires_at ? code.expires_at.slice(0, 10) : '',
  );
  const [region, setRegion] = useState<string>(code.region ?? '');
  const [recommendedTeamSize, setRecommendedTeamSize] = useState<string>(
    code.recommended_team_size ?? '',
  );
  const [note, setNote] = useState<string>(code.note ?? '');
  const [submitting, setSubmitting] = useState(false);

  // OCP-583 - explicit, per-save opt-in to edit grant fields on a REDEEMED
  // code. Off by default; checking it unlocks the grant inputs and sends
  // override_grant_lock so the server applies + audit-logs the override.
  const [overrideGrantLock, setOverrideGrantLock] = useState(false);
  // OCP-599 - the override on a redeemed code must say WHY (>= 10 chars);
  // the api refuses it otherwise (400 override_reason_required) and records
  // the reason in the code.grant_lock_override audit entry.
  const [overrideReason, setOverrideReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const grantLocked = used && !overrideGrantLock;
  const overriding = used && overrideGrantLock;

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    if (overriding && overrideReason.trim().length < 10) {
      setReasonError('Give a reason of at least 10 characters - it is written to the audit log.');
      return;
    }
    setReasonError(null);
    setSubmitting(true);
    try {
      // Validity + assignment - always editable.
      const patch: Record<string, unknown> = {
        maxUses,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        category,
        region: region.trim() || null,
        recommendedTeamSize: recommendedTeamSize.trim() || null,
        note: note.trim() || null,
      };
      // Grant fields for an unused code, or a redeemed one under the
      // explicit override (server audit-logs before/after).
      if (!used || overrideGrantLock) {
        patch.tier = tier;
        patch.duration = duration;
        patch.paid = paid;
        patch.paidAmountEur = paid && paidAmountEur.trim() ? Number(paidAmountEur) : null;
        patch.seats = seats.trim() ? Number(seats) : null;
        patch.storageQuotaGbOverride = storage.trim() ? Number(storage) : null;
        if (overriding) {
          patch.override_grant_lock = true;
          patch.reason = overrideReason.trim();
        }
      }
      await onSave(code.id, patch);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 px-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-xl"
      >
        <div className="mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Edit code</h2>
          <p className="mt-0.5 font-mono text-xs text-gray-500">{code.code}</p>
          {used && (
            <div className="mt-1">
              <p className="text-[11px] text-amber-600 dark:text-amber-400">
                This code has been redeemed ({code.used_count}/{code.max_uses}). Grant fields are
                locked - a change does not rebuild the provisioned workspace, but it DOES change
                what the entitlement resolvers read from now on (AI allowance tier x seats, seat
                caps). Validity + assignment are always editable.
              </p>
              <label className="mt-1.5 flex items-center gap-2 text-[11px] text-amber-700 dark:text-amber-300">
                <input
                  type="checkbox"
                  data-testid="override-grant-lock"
                  checked={overrideGrantLock}
                  onChange={(e) => setOverrideGrantLock(e.target.checked)}
                />
                Override the grant lock for this save (deliberate entitlement change - audit-logged
                with before/after)
              </label>
              {overrideGrantLock && (
                <div className="mt-1.5">
                  <textarea
                    data-testid="override-reason"
                    value={overrideReason}
                    rows={2}
                    maxLength={500}
                    placeholder="Why does the grant change? (min 10 chars, audit-logged)"
                    onChange={(e) => setOverrideReason(e.target.value)}
                    className="w-full rounded border border-amber-300 dark:border-amber-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
                  />
                  {reasonError && (
                    <p role="alert" className="mt-1 text-[11px] text-red-600 dark:text-red-400">
                      {reasonError}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <form onSubmit={submit} className="flex flex-col gap-4 text-sm">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ModalRow label="Tier">
              <SelectLike value={tier} options={TIER_OPTIONS} onChange={setTier} disabled={grantLocked} />
            </ModalRow>
            <ModalRow label="Duration">
              <select
                data-testid="edit-duration-select"
                value={duration}
                disabled={grantLocked}
                onChange={(e) => setDuration(e.target.value as LicenseDurationValue)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
              >
                {LICENSE_DURATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </ModalRow>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ModalRow label="Seats (optional)">
              <input
                type="number"
                min={1}
                max={1000}
                value={seats}
                disabled={grantLocked}
                onChange={(e) => setSeats(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
              />
            </ModalRow>
            <ModalRow label="Storage GB override (optional)">
              <input
                type="number"
                min={0}
                value={storage}
                disabled={grantLocked}
                onChange={(e) => setStorage(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
              />
            </ModalRow>
          </div>
          <label className={`flex items-center gap-2 text-sm ${used ? 'opacity-50' : ''}`}>
            <input
              type="checkbox"
              checked={paid}
              disabled={grantLocked}
              onChange={(e) => setPaid(e.target.checked)}
              className="h-4 w-4 accent-brand-500"
            />
            <span className="text-gray-700 dark:text-gray-300">Paid code</span>
          </label>
          {paid && (
            <ModalRow label="Amount EUR">
              <input
                type="number"
                step="0.01"
                min={0}
                value={paidAmountEur}
                disabled={grantLocked}
                onChange={(e) => setPaidAmountEur(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
              />
            </ModalRow>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <ModalRow label="Max uses">
              <input
                type="number"
                min={1}
                max={100}
                value={maxUses}
                onChange={(e) => setMaxUses(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            </ModalRow>
            <ModalRow label="Expires (blank = never)">
              <input
                type="date"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
              />
            </ModalRow>
          </div>
          <ModalRow label="Category">
            <SelectLike value={category} options={CATEGORY_OPTIONS} onChange={setCategory} />
          </ModalRow>
          <ModalRow label="Region (blank = auto)">
            <input
              type="text"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="e.g. eu-central"
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </ModalRow>
          <ModalRow label="Recommended team-size">
            <select
              value={recommendedTeamSize}
              onChange={(e) => setRecommendedTeamSize(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            >
              {TEAM_SIZE_OPTIONS.map((o) => (
                <option key={o} value={o}>
                  {o || '(none)'}
                </option>
              ))}
            </select>
          </ModalRow>
          <ModalRow label="Note">
            <textarea
              value={note}
              rows={2}
              maxLength={500}
              onChange={(e) => setNote(e.target.value)}
              className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </ModalRow>

          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ModalRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs text-gray-600 dark:text-gray-400">{label}</span>
      {children}
    </label>
  );
}

function SelectLike<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: readonly T[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as T)}
      className="w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm focus:border-brand-500 focus:outline-none disabled:opacity-50"
    >
      {options.map((o) => (
        <option key={o} value={o}>
          {o.replace(/_/g, ' ')}
        </option>
      ))}
    </select>
  );
}
